<template>
    <soft-modal :modalActive="true" @close="$emit('close')">
        <div class="text-center p-3">
            <h6 class="font-weight-bolder mb-1">
                {{ cell.portal.name }}
            </h6>
            <p class="text-sm text-secondary mb-3">
                {{ cell.user.name }}
                <span v-if="cell.user.email" class="d-block text-xxs">{{ cell.user.email }}</span>
            </p>

            <!-- Current status display -->
            <div class="mb-3">
                <template v-if="cell.type">
                    <span class="badge me-2"
                        :class="cell.type === 'propia' ? 'bg-gradient-success' : 'bg-gradient-info'">
                        {{ cell.type === 'propia' ? 'Credencial Propia' : 'Credencial Compartida' }}
                    </span>
                    <span v-if="cell.status" class="text-sm"
                        :class="cell.status === 'active' ? 'text-success' : 'text-danger'">
                        <i class="fas fa-circle me-1" style="font-size: 7px;"></i>
                        {{ cell.status === 'active' ? 'Activo' : 'Inactivo' }}
                    </span>
                </template>
                <template v-else>
                    <span class="text-secondary text-sm">
                        <i class="fas fa-minus-circle me-1"></i>
                        Sin credenciales configuradas
                    </span>
                </template>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex flex-column gap-2">
                <!-- No credential: Create + Assign -->
                <template v-if="!cell.type">
                    <soft-button color="success" variant="gradient" size="sm"
                        class="w-100" @click="$emit('create')">
                        <i class="fas fa-plus me-2"></i> Crear Credencial
                    </soft-button>
                    <soft-button v-if="showAssignOption" color="info" variant="gradient" size="sm"
                        class="w-100" @click="$emit('assign')">
                        <i class="fas fa-share me-2"></i> Compartir Credencial
                    </soft-button>
                </template>

                <!-- Own credential (propia) -->
                <template v-else-if="cell.type === 'propia'">
                    <soft-button color="warning" variant="gradient" size="sm"
                        class="w-100" @click="$emit('edit')">
                        <i class="fas fa-edit me-2"></i> Editar Credencial
                    </soft-button>
                    <soft-button
                        :color="cell.status === 'active' ? 'secondary' : 'success'"
                        variant="outline" size="sm" class="w-100"
                        @click="$emit('toggle-status')">
                        <i class="fas fa-power-off me-2"></i>
                        {{ cell.status === 'active' ? 'Desactivar Sync' : 'Activar Sync' }}
                    </soft-button>
                    <soft-button color="danger" variant="outline" size="sm"
                        class="w-100" @click="confirmDelete">
                        <i class="fas fa-trash me-2"></i> Eliminar Credencial
                    </soft-button>
                </template>

                <!-- Shared credential (compartida) -->
                <template v-else-if="cell.type === 'compartida'">
                    <soft-button
                        :color="cell.status === 'active' ? 'secondary' : 'success'"
                        variant="outline" size="sm" class="w-100"
                        @click="$emit('toggle-status')">
                        <i class="fas fa-power-off me-2"></i>
                        {{ cell.status === 'active' ? 'Desactivar Sync' : 'Activar Sync' }}
                    </soft-button>
                    <soft-button color="danger" variant="outline" size="sm"
                        class="w-100" @click="confirmRevoke">
                        <i class="fas fa-ban me-2"></i> Revocar Asignación
                    </soft-button>
                </template>
            </div>

            <!-- Confirm delete -->
            <div v-if="confirmingDelete" class="mt-3 p-3 border rounded bg-light">
                <p class="text-sm text-danger mb-2 font-weight-bold">
                    <i class="fas fa-exclamation-triangle me-1"></i>
                    ¿Estás seguro de eliminar esta credencial?
                </p>
                <div class="d-flex gap-2 justify-content-center">
                    <soft-button color="secondary" variant="outline" size="sm"
                        @click="confirmingDelete = false">
                        Cancelar
                    </soft-button>
                    <soft-button color="danger" variant="gradient" size="sm"
                        @click="$emit('delete')">
                        Sí, eliminar
                    </soft-button>
                </div>
            </div>

            <!-- Confirm revoke -->
            <div v-if="confirmingRevoke" class="mt-3 p-3 border rounded bg-light">
                <p class="text-sm text-danger mb-2 font-weight-bold">
                    <i class="fas fa-exclamation-triangle me-1"></i>
                    ¿Revocar la credencial compartida?
                </p>
                <div class="d-flex gap-2 justify-content-center">
                    <soft-button color="secondary" variant="outline" size="sm"
                        @click="confirmingRevoke = false">
                        Cancelar
                    </soft-button>
                    <soft-button color="danger" variant="gradient" size="sm"
                        @click="$emit('revoke')">
                        Sí, revocar
                    </soft-button>
                </div>
            </div>
        </div>
    </soft-modal>
</template>

<script>
import { ref } from "vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftModal from "@/components/SoftModal.vue";

export default {
    name: "CellActions",
    components: { SoftButton, SoftModal },
    props: {
        cell: { type: Object, required: true },
        showAssignOption: { type: Boolean, default: false },
    },
    emits: ['close', 'create', 'edit', 'delete', 'revoke', 'toggle-status', 'assign'],
    setup() {
        const confirmingDelete = ref(false);
        const confirmingRevoke = ref(false);

        const confirmDelete = () => {
            confirmingDelete.value = true;
            confirmingRevoke.value = false;
        };

        const confirmRevoke = () => {
            confirmingRevoke.value = true;
            confirmingDelete.value = false;
        };

        return { confirmingDelete, confirmingRevoke, confirmDelete, confirmRevoke };
    }
};
</script>

<style scoped>
.gap-2 {
    gap: 0.5rem;
}
</style>
